import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { authService, usersService } from 'services';
import { captureError, identifyUser } from 'services/monitoring';
import { guessTimezoneLabel } from 'features/scheduling/helpers';

/**
 * AuthContext holds the canonical auth state for the app: the Firebase
 * user, their Firestore profile, their resolved role, and a loading flag
 * while auth resolves. Route guards and the rest of the UI consume this via
 * `useAuth()`.
 *
 * Flow:
 *   1. On mount we subscribe to Firebase auth state (survives refreshes
 *      thanks to local persistence — see services/firebase/config).
 *   2. On every change we hydrate the user's profile from `users/{uid}`.
 *      Accounts are ADMIN-PROVISIONED — there is no public sign-up — so a
 *      signed-in user with **no profile** is unprovisioned: we sign the session
 *      straight back out and surface `authNotice` for the login page. This makes
 *      any stray account (e.g. one created directly against the Auth API) inert.
 *   3. login / logout / resetPassword are exposed so screens never touch
 *      Firebase directly.
 */
export const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Message shown on the login page when a session was rejected (e.g. an
  // unprovisioned account). Set before we force-sign-out; the null re-fire of
  // the listener does NOT clear it, so the notice survives to be displayed.
  const [authNotice, setAuthNotice] = useState(null);

  useEffect(() => {
    const unsubscribe = authService.observe(async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setProfile(null);
        setRole(null);
        setIsLoading(false);
        identifyUser(null); // don't attribute the next visitor's errors to the last one
        return;
      }

      try {
        const userProfile = await usersService.get(firebaseUser.uid);

        // No admin-provisioned profile → not allowed in.
        if (!userProfile) {
          setAuthNotice(
            "This account isn't set up yet. Please contact your administrator to get access."
          );
          await authService.logout(); // re-fires the listener with null
          return;
        }

        // Backfill a personal timezone for accounts created before the field
        // existed, so class times always render in a real zone.
        let hydrated = userProfile;
        if (!hydrated.timezone) {
          const timezone = guessTimezoneLabel();
          usersService.update(firebaseUser.uid, { timezone }).catch(() => {});
          hydrated = { ...hydrated, timezone };
        }

        setProfile(hydrated);
        setRole(hydrated.role || null);
        setUser(firebaseUser);
        setIsLoading(false);

        // Tag subsequent reports with uid + role, so an incident reads as
        // "every mentor" rather than "someone". No name or email is sent.
        identifyUser({ uid: firebaseUser.uid, role: hydrated.role });
      } catch (error) {
        // Firestore unreachable: don't grant a role we couldn't verify. The user
        // stays signed in but role-less (RoleRoute sends them to /unauthorized);
        // a refresh retries the hydration.
        //
        // Worth reporting: to the user this looks like being locked out of
        // their own account with no explanation, and nothing else in the app
        // would ever tell us it happened.
        captureError(error, { kind: 'profile-hydration', uid: firebaseUser.uid });
        // eslint-disable-next-line no-console
        console.error('[auth] Failed to hydrate profile:', error);
        setProfile(null);
        setRole(null);
        setUser(firebaseUser);
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  /**
   * Sign in with an email + password. (Username sign-in resolves to an email
   * first, in the login page, via the `accountLogin` callable.) We don't
   * navigate here — the listener updates state and PublicOnlyRoute redirects.
   */
  const login = useCallback((email, password) => {
    setAuthNotice(null);
    return authService.login(email, password);
  }, []);

  const logout = useCallback(() => authService.logout(), []);

  const resetPassword = useCallback((email) => authService.resetPassword(email), []);

  /**
   * Change the current user's password (re-auth + update). On success we stamp
   * the profile so the admin Accounts page reflects "Set by user" — a client
   * `updatePassword` doesn't bump the auth token time the reset-link flow uses.
   */
  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      await authService.changePassword(currentPassword, newPassword);
      if (user) {
        usersService.markPasswordChanged(user.uid).catch(() => {});
        setProfile((prev) => (prev ? { ...prev, credentialStatus: 'user-set' } : prev));
      }
    },
    [user]
  );

  const clearAuthNotice = useCallback(() => setAuthNotice(null), []);

  /** Patch the current user's profile (e.g. timezone) and reflect it locally. */
  const updateProfile = useCallback(
    async (patch) => {
      if (!user) return;
      await usersService.update(user.uid, patch);
      setProfile((prev) => (prev ? { ...prev, ...patch } : prev));
    },
    [user]
  );

  const value = useMemo(
    () => ({
      user,
      profile,
      role,
      isAuthenticated: Boolean(user),
      isLoading,
      authNotice,
      clearAuthNotice,
      login,
      logout,
      resetPassword,
      changePassword,
      updateProfile,
    }),
    [user, profile, role, isLoading, authNotice, clearAuthNotice, login, logout, resetPassword, changePassword, updateProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
